import { LinearProgress } from '@mui/material'
import MapView from './MapView'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import {
    default_FM_Tab,
    setIsFMApplicationEnabled,
} from '../../../redux/slices/facilityMonitoringSlice'
import { SiteView } from './SiteView/SiteView'
import FacilityMonitoringSettings from './Settings'
import {
    UserPermissions,
    FMTABS,
    URLSearchParamsKeys,
    Applications,
    RoutePaths,
} from '../../../constants/constants'
import Dashboard from '../../Common/Dashboard'
import { findEntityById } from '../../../utils/EntityUtils'
import useFMAppInitializer from './hooks/useFMAppInitializer'
import { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import Reports from './Reports/Reports'
import AssetEntityList from './AssetView/AssetsEntityList'
import {
    a11yProps,
    CustomTab,
    CustomTabPanel,
    CustomTabs,
} from '../../Common/MuiTabs'
import DeviceEntityList from './DeviceView/DeviceEntityList'
import { useTranslation } from 'react-i18next'
import BusinessRules from '../../DTStudio/BusinessRules'
import { updateSelectedEntity } from '../../../redux/slices/dtStudioSlice'
import { usePermissions } from '../../../hooks/usePermissions'

const FMTabs = (): ReactElement => {
    const { t } = useTranslation()
    const fmSlice = useAppSelector((state) => state.facilityMonitoring)
    const entityTemplatesList = useAppSelector((state) => state.nav.navList)
    const { hasPermission } = usePermissions()
    const hasPermissionAccess = useMemo(
        () =>
            hasPermission([
                UserPermissions.ORG_ADMIN,
                UserPermissions.SYSTEM_READER,
            ]),
        [hasPermission],
    )
    const hasRulesTabAccess = useMemo(
        () =>
            hasPermission([
                UserPermissions.USER_ADMIN,
                UserPermissions.SYSTEM_READER,
            ]),
        [hasPermission],
    )
    const hasWriteAccess = useMemo(
        () => hasPermission([UserPermissions.ENTITY_WRITER]),
        [hasPermission],
    )
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const fmAppStatus = useFMAppInitializer()
    const { loadingFMApp, errorInLoading, isFMAppEnabled, isReportsEnabled } =
        fmAppStatus
    const {
        isFMApplicationEnabled,
        selectedBuilding,
        siteEntityTemplateId,
        siteList,
        assetEntityTemplateId,
        sensorEntityTemplateId,
        analyticsDashboardId,
        scheduledReportsEntityTemplateId,
    } = fmSlice

    const [searchParams, setSearchParams] = useSearchParams()
    const tabIndexParam = searchParams.get(URLSearchParamsKeys.tabIndex)
    const selectedSiteUID = searchParams.get(
        URLSearchParamsKeys.selectedSiteUID,
    )
    const selectedBuildingUID = searchParams.get(
        URLSearchParamsKeys.selectedBuildingUID,
    )
    const tabIndex = tabIndexParam
        ? Number.parseInt(tabIndexParam)
        : default_FM_Tab

    const { name: siteEntityTemplateIdName } = findEntityById(
        entityTemplatesList,
        siteEntityTemplateId,
    )

    useEffect(() => {
        if (tabIndex === FMTABS.RULES) {
            const { entity: assetEntityTemplate } = findEntityById(
                entityTemplatesList,
                assetEntityTemplateId,
            )
            dispatch(updateSelectedEntity(assetEntityTemplate))
        }
    }, [assetEntityTemplateId, entityTemplatesList, dispatch, tabIndex])

    const updateTab = useCallback(
        (newValue: number) => {
            const dynamicParams: Record<string, string> = {}
            searchParams.forEach((value, key) => {
                if (key.startsWith(URLSearchParamsKeys.dynamicLevelPrefix)) {
                    dynamicParams[key] = value
                }
            })

            const newParams: Record<string, string> = {
                ...dynamicParams,
                [URLSearchParamsKeys.applicationId]:
                    Applications.facilityMonitoring.id,
                [URLSearchParamsKeys.tabIndex]: newValue.toString(),
                [URLSearchParamsKeys.selectedSiteUID]:
                    selectedSiteUID ?? siteList[0]?.uid ?? '',
                [URLSearchParamsKeys.selectedBuildingUID]:
                    selectedBuildingUID ?? selectedBuilding?.uid ?? '',
            }

            if (newValue === FMTABS.RULES) {
                const { entity: assetEntityTemplate } = findEntityById(
                    entityTemplatesList,
                    assetEntityTemplateId,
                )
                dispatch(updateSelectedEntity(assetEntityTemplate))
            }

            setSearchParams(newParams)
        },
        [
            setSearchParams,
            selectedSiteUID,
            searchParams,
            siteList,
            selectedBuildingUID,
            selectedBuilding,
            entityTemplatesList,
            assetEntityTemplateId,
            dispatch,
        ],
    )

    const handleChange = useCallback(
        (event: React.SyntheticEvent, newValue: number) => {
            updateTab(newValue)
        },
        [updateTab],
    )

    useEffect(() => {
        if (loadingFMApp) {
            /**
             * After the change of organization, we fetch latest permissions.
             * Till we fetch the latest permissions, we should wait here.
             * Because in the latest permissions only we will have updated hasPermissionAccess value
             */
            return
        }
        if (errorInLoading || !isFMAppEnabled) {
            if (hasPermissionAccess) {
                setSearchParams({
                    [URLSearchParamsKeys.tabIndex]: FMTABS.SETTINGS.toString(),
                })
                dispatch(setIsFMApplicationEnabled(false))
            } else {
                navigate(`/`)
            }
        }
    }, [
        loadingFMApp,
        errorInLoading,
        isFMAppEnabled,
        dispatch,
        hasPermissionAccess,
        navigate,
        setSearchParams,
    ])

    if (loadingFMApp) {
        return <LinearProgress />
    }

    if (!hasPermissionAccess && tabIndex === FMTABS.SETTINGS) {
        return <Navigate to={RoutePaths.facilityMonitoring} />
    }

    if (!hasRulesTabAccess && tabIndex === FMTABS.RULES) {
        return <Navigate to={RoutePaths.facilityMonitoring} />
    }

    return (
        <>
            <CustomTabs
                value={tabIndex}
                onChange={handleChange}
                aria-label={t('basictabs')}
                TabIndicatorProps={{
                    children: <span />,
                }}
            >
                <CustomTab
                    label={t('dashboard')}
                    {...a11yProps(FMTABS.DASHBOARD)}
                    disabled={
                        isFMApplicationEnabled === false ||
                        !analyticsDashboardId
                    }
                />
                <CustomTab
                    label={t('mapView')}
                    {...a11yProps(FMTABS.MAP_VIEW)}
                    disabled={isFMApplicationEnabled === false}
                />
                <CustomTab
                    label={
                        siteEntityTemplateIdName
                            ? t('entityView', {
                                  entity: t(siteEntityTemplateIdName),
                              })
                            : t('siteView')
                    }
                    {...a11yProps(FMTABS.SITE_VIEW)}
                    disabled={isFMApplicationEnabled === false}
                />
                <CustomTab
                    label={t('assetView')}
                    {...a11yProps(FMTABS.ASSET_VIEW)}
                    disabled={isFMApplicationEnabled === false}
                />
                <CustomTab
                    label={t('deviceView')}
                    {...a11yProps(FMTABS.DEVICE_VIEW)}
                    disabled={isFMApplicationEnabled === false}
                />
                <CustomTab
                    label={t('reports')}
                    {...a11yProps(FMTABS.REPORTS)}
                    disabled={isReportsEnabled === false}
                />

                {hasRulesTabAccess && (
                    <CustomTab
                        label={t('rules')}
                        {...a11yProps(FMTABS.RULES)}
                        disabled={isReportsEnabled === false}
                    />
                )}
                {hasPermissionAccess && (
                    <CustomTab
                        label={t('settings')}
                        {...a11yProps(FMTABS.SETTINGS)}
                    />
                )}
            </CustomTabs>
            <CustomTabPanel value={tabIndex} index={FMTABS.DASHBOARD}>
                <Dashboard analyticsDashboardId={analyticsDashboardId} />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={FMTABS.MAP_VIEW}>
                <MapView />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={FMTABS.SITE_VIEW}>
                <SiteView />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={FMTABS.ASSET_VIEW}>
                <AssetEntityList
                    assetEntityTemplateId={assetEntityTemplateId}
                />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={FMTABS.DEVICE_VIEW}>
                <DeviceEntityList
                    sensorEntityTemplateId={sensorEntityTemplateId}
                />
            </CustomTabPanel>

            <CustomTabPanel value={tabIndex} index={FMTABS.REPORTS}>
                <Reports
                    scheduledReportsEntityTemplateId={
                        scheduledReportsEntityTemplateId
                    }
                />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={FMTABS.RULES}>
                <BusinessRules maxHeight="calc(100vh - 220px)" />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={FMTABS.SETTINGS}>
                <FacilityMonitoringSettings hasWriteAccess={hasWriteAccess} />
            </CustomTabPanel>
        </>
    )
}

export default FMTabs
